import { LOSS_BURST_KEYFRAME_MIN_INTERVAL_MS, RECOVERY_HEALTHY_HOLD_MS, SUSTAINED_RTT_WINDOW_MS,
  isTuningPatch } from '@playcastdotio/PeerCore';
import type { TuningIdentity, TuningPatch, TuningSettingKey, TuningSettingReceipt } from '@playcastdotio/PeerCore';

export type RuntimeRecoveryPolicySnapshot = {
  policyVersion: string; mode: 'production' | 'fixed-application';
  keyframePolicy: 'production' | 'required-only' | 'loss-repair';
  lossKeyframeMinIntervalMs: number; unhealthyHoldMs: number; healthyHoldMs: number;
};
export interface IRuntimeRecoveryPolicy {
  snapshot(peerId: string): RuntimeRecoveryPolicySnapshot;
  apply(target: TuningIdentity, patch: TuningPatch): Promise<readonly TuningSettingReceipt[]>;
  restore(target: TuningIdentity): Promise<void>;
}
const production = Object.freeze({ policyVersion: 'production-v1', mode: 'production', keyframePolicy: 'production',
  lossKeyframeMinIntervalMs: LOSS_BURST_KEYFRAME_MIN_INTERVAL_MS,
  unhealthyHoldMs: SUSTAINED_RTT_WINDOW_MS, healthyHoldMs: RECOVERY_HEALTHY_HOLD_MS } as const);
const fields = { 'recovery.mode': 'mode', 'recovery.keyframePolicy': 'keyframePolicy',
  'recovery.lossKeyframeMinIntervalMs': 'lossKeyframeMinIntervalMs', 'recovery.unhealthyHoldMs': 'unhealthyHoldMs',
  'recovery.healthyHoldMs': 'healthyHoldMs' } as const;

/** Per-peer immutable policy snapshots; production constants remain unchanged. */
export class RuntimeRecoveryPolicy implements IRuntimeRecoveryPolicy {
  private readonly policies = new Map<string, { target: TuningIdentity; snapshot: RuntimeRecoveryPolicySnapshot }>();
  public snapshot(peerId: string): RuntimeRecoveryPolicySnapshot { return this.policies.get(peerId)?.snapshot ?? production; }
  public async apply(target: TuningIdentity, patch: TuningPatch): Promise<readonly TuningSettingReceipt[]> {
    if (!isTuningPatch(patch)) throw new Error('invalid-recovery-policy');
    const prior = this.policies.get(target.peerId);
    if (prior && (prior.target.connectionEpoch > target.connectionEpoch ||
      (prior.target.connectionEpoch === target.connectionEpoch && prior.target.configRevision > target.configRevision))) {
      throw new Error('stale-recovery-policy');
    }
    const next = { ...this.snapshot(target.peerId), policyVersion: `tuning-${target.configRevision}` };
    const receipts: TuningSettingReceipt[] = [];
    for (const [key, field] of Object.entries(fields)) {
      const value = patch[key as TuningSettingKey];
      if (value === undefined) continue;
      Object.assign(next, { [field]: value });
      receipts.push({ key: key as TuningSettingKey, requested: value, accepted: value, readback: value, status: 'applied' });
    }
    this.policies.set(target.peerId, { target: { ...target }, snapshot: Object.freeze(next) });
    return receipts;
  }
  public async restore(target: TuningIdentity): Promise<void> {
    const prior = this.policies.get(target.peerId);
    if (prior && prior.target.connectionEpoch !== target.connectionEpoch) throw new Error('stale-recovery-policy');
    this.policies.delete(target.peerId);
  }
}
